(() => {
  if (customElements.get('njt-brand-carousel')) return;
  customElements.define('njt-brand-carousel', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.track = this.querySelector('[data-brand-track]');
      this.search = this.querySelector('[data-brand-search]');
      this.status = this.querySelector('[data-brand-status]');
      this.pauseButton = this.querySelector('[data-brand-pause]');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.paused = this.motion.matches;
      this.hovered = false; this.visible = false; this.dragging = false;
      this.keyboardFocused = false; this.pageSuspended = false; this.interactedUntil = 0;
      this.animation = null; this.resumeTimer = null; this.lastFrame = null;
      this.frame = now => {
        const elapsed = this.lastFrame === null ? 0 : Math.min(50, now - this.lastFrame);
        this.lastFrame = now;
        this.position = (this.position + elapsed * 0.040) % this.loopWidth;
        this.track.scrollLeft = this.position;
        this.animation = requestAnimationFrame(this.frame);
      };
      this.refreshItems();
      this.querySelector('[data-brand-controls]').hidden = false;
      this.listen(this.search, 'input', () => this.filter(), options);
      this.listen(this.querySelector('[data-brand-prev]'), 'click', () => this.advance(-1), options);
      this.listen(this.querySelector('[data-brand-next]'), 'click', () => this.advance(1), options);
      this.listen(this.pauseButton, 'click', () => {this.paused = !this.paused; this.updatePause(); this.syncMotion();}, options);
      this.listen(this.track, 'pointerenter', e => {if (e.pointerType === 'mouse') {this.hovered = true; this.syncMotion();}}, options);
      this.listen(this.track, 'pointerleave', () => {this.hovered = false; this.syncMotion();}, options);
      this.listen(this.track, 'pointerdown', () => {this.dragging = true; this.holdMotion();}, options);
      const release = () => {if (this.dragging) {this.dragging = false; this.holdMotion();}};
      this.listen(window, 'pointerup', release, options);
      this.listen(window, 'pointercancel', release, options);
      this.listen(this.track, 'wheel', e => {if (Math.abs(e.deltaX) > Math.abs(e.deltaY) || e.shiftKey) this.holdMotion();}, {...options, passive:true});
      const focusChanged = () => queueMicrotask(() => {
        this.keyboardFocused = this.contains(document.activeElement) && document.activeElement.matches(':focus-visible');
        this.syncMotion();
      });
      this.listen(this, 'focusin', focusChanged, options);
      this.listen(this, 'focusout', focusChanged, options);
      this.listen(this, 'keydown', focusChanged, options);
      this.listen(this.motion, 'change', () => {if(this.motion.matches) this.paused = true; this.updatePause(); this.syncMotion();}, options);
      this.listen(document, 'visibilitychange', () => this.syncMotion(), options);
      this.listen(window, 'pagehide', () => {this.pageSuspended = true; this.syncMotion();}, options);
      this.listen(window, 'pageshow', () => {this.pageSuspended = false; this.syncMotion();}, options);
      this.observer = new IntersectionObserver(entries => {this.visible = entries[0].isIntersecting; this.syncMotion();});
      this.observer.observe(this);
      this.resizeObserver = new ResizeObserver(() => this.measureLoop());
      this.resizeObserver.observe(this.track);
      this.updatePause();
      this.loadRemaining();
    }
    listen(target, name, callback, options) { target.addEventListener(name, callback, options); }
    stopMotion() {
      cancelAnimationFrame(this.animation);
      clearTimeout(this.resumeTimer);
      this.animation = null; this.resumeTimer = null; this.lastFrame = null;
    }
    syncMotion() {
      clearTimeout(this.resumeTimer);
      this.resumeTimer = null;
      if (!this.isConnected || !this.controller || !this.visible || document.hidden || this.pageSuspended || this.paused ||
        this.motion.matches || this.hovered || this.dragging || this.keyboardFocused || this.searching || !this.loopWidth) {
        this.stopMotion();
        return;
      }
      const remaining = this.interactedUntil - Date.now();
      if (remaining > 0) {
        this.stopMotion();
        this.resumeTimer = setTimeout(() => this.syncMotion(), remaining);
        return;
      }
      if (this.animation === null) {
        this.position = this.track.scrollLeft;
        this.lastFrame = null;
        this.animation = requestAnimationFrame(this.frame);
      }
    }
    holdMotion() {
      this.interactedUntil = Date.now() + 2000;
      this.syncMotion();
    }
    refreshItems() {
      this.stopMotion();
      const previousPosition = this.track.scrollLeft;
      this.querySelectorAll('[data-brand-clone]').forEach(item => item.remove());
      this.items = [...this.querySelectorAll('[data-brand-item]')];
      const eligible = this.items.filter(item => !item.hasAttribute('data-njb-excluded'));
      this.clones = eligible.length > 6 ? eligible.slice(0, 8).map(item => {
        const clone = item.cloneNode(true);
        clone.removeAttribute('data-brand-item');
        clone.removeAttribute('data-njb-fitment');
        clone.setAttribute('data-brand-clone', '');
        clone.setAttribute('aria-hidden', 'true');
        clone.querySelectorAll('a').forEach(link => link.tabIndex = -1);
        this.track.append(clone);
        return clone;
      }) : [];
      this.filter(false);
      this.position = this.loopWidth ? previousPosition % this.loopWidth : previousPosition;
      this.track.scrollLeft = this.position;
    }
    measureLoop() {
      const first = this.items.find(item => !item.hidden);
      this.loopWidth = this.clones?.length && !this.searching && first
        ? Math.max(0, this.clones[0].offsetLeft - first.offsetLeft) : 0;
      this.syncMotion();
    }
    normalize(value) { return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase().replace(/[^a-z0-9]/g, ''); }
    filter(reset = true) {
      this.stopMotion();
      const term = this.normalize(this.search.value);
      this.searching = Boolean(this.search.value.trim());
      let count = 0;
      this.items.forEach(item => {
        item.hidden = item.hasAttribute('data-njb-excluded') || !this.normalize(item.dataset.brandName).includes(term);
        if (!item.hidden) count++;
      });
      this.clones?.forEach(item => item.hidden = Boolean(term));
      if (reset) {
        this.track.scrollTo({left:0,behavior:'instant'});
        this.position = 0;
      }
      this.measureLoop();
      this.status.textContent = count ? this.status.dataset.countLabel.replace('{count}', count) : this.status.dataset.emptyLabel;
      this.querySelectorAll('[data-brand-prev],[data-brand-next]').forEach(button => button.disabled = count < 2);
    }
    updatePause() {
      this.pauseButton.setAttribute('aria-pressed', String(this.paused));
      this.pauseButton.textContent = this.paused ? this.pauseButton.dataset.playLabel : this.pauseButton.dataset.pauseLabel;
    }
    advance(direction, automatic = false) {
      const cards = this.items.filter(item => !item.hidden);
      if (!cards.length) return;
      if (!automatic) this.holdMotion();
      const step = cards[0].offsetWidth + (parseFloat(getComputedStyle(this.track).columnGap) || 0);
      const max = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const atEnd = this.track.scrollLeft >= max - 2;
      const atStart = this.track.scrollLeft <= 2;
      const left = direction > 0 && atEnd ? 0 : direction < 0 && atStart ? max : Math.max(0,Math.min(max,this.track.scrollLeft + step * direction));
      this.track.scrollTo({left, behavior:this.motion.matches || (direction > 0 && atEnd) || (direction < 0 && atStart) ? 'instant' : 'smooth'});
    }
    async loadRemaining() {
      let more = this.querySelector('[data-brand-more]');
      const seen = new Set();
      try {
        while (more && this.isConnected) {
          const url = new URL(more.href, location.origin);
          if (url.origin !== location.origin || seen.has(url.href)) break;
          seen.add(url.href);
          url.searchParams.set('section_id', this.dataset.sectionId);
          const response = await fetch(url, {signal:this.controller.signal});
          if (!response.ok) break;
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const section = doc.querySelector('njt-brand-carousel');
          if (!section) break;
          const known = new Set(this.items.map(item => item.querySelector('a').getAttribute('href')));
          section.querySelectorAll('[data-brand-item]').forEach(item => {
            if (!known.has(item.querySelector('a').getAttribute('href'))) this.track.append(item);
          });
          const next = section.querySelector('[data-brand-more]');
          if (next) more.href = next.href;
          else {more.remove();more = null;}
          this.refreshItems();
        }
      } catch (_) {}
    }
    disconnectedCallback() {
      this.controller?.abort(); this.controller = null;
      this.observer?.disconnect(); this.resizeObserver?.disconnect();
      this.stopMotion();
    }
  });
})();
